/**
 * ReceiverValidationPanel — semáforo de ventas YA COMPRADAS cuyo envío ML entró
 * en verificación de documentación del comprador (CBT puerta a puerta).
 * 🟡 verificando · 🟠 sin cupo · 🟢 liberada · 🔴 cancelada/claim.
 * 📦 = el paquete ya llegó al depósito (escaneado). Las filas desaparecen solas
 * al procesarse en el depósito (o convertirse a stock si terminó cancelada).
 * Solo lectura: la gestión de canceladas vive en CancelledCasesPanel.
 */
import { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { api } from '../../api/client';

interface RVCase {
  id: number;
  ml_order_id: string;
  pack_id: string | null;
  asin: string | null;
  amount: string | null;
  detected_at: string;
  arrived_at_wh: string | null;
  stage: 'validating' | 'no_quota' | 'released' | 'cancelled' | 'claim';
  title: string | null;
  thumbnail: string | null;
  quantity: number | null;
}

interface CasesData {
  total: number;
  counts: Record<string, number>;
  cases: RVCase[];
}

const STAGE_LABEL: Record<string, string> = {
  validating: 'Verificando documentación',
  no_quota: 'Sin cupo del comprador',
  released: 'Liberada — lista para despachar',
  cancelled: 'Cancelada',
  claim: 'Con reclamo',
};

const STAGE_BADGE: Record<string, string> = {
  validating: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  no_quota: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  released: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  claim: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const STAGE_ROW: Record<string, string> = {
  validating: 'bg-yellow-50/60 dark:bg-yellow-900/10',
  no_quota: 'bg-orange-50/60 dark:bg-orange-900/10',
  released: 'bg-green-50/60 dark:bg-green-900/10',
  cancelled: 'bg-red-50/60 dark:bg-red-900/10',
  claim: 'bg-red-50/60 dark:bg-red-900/10',
};

function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

export function ReceiverValidationPanel({ sellerId }: { sellerId: number }) {
  const [data, setData] = useState<CasesData | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<CasesData>(
        `/amazon/receiver-validation/cases?seller_id=${sellerId}`
      );
      if (res.success && res.data) setData(res.data);
      else setData(null);
    } catch {
      setData(null); // panel informativo: sin toast
    }
  }, [sellerId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data || data.total === 0) return null;

  return (
    <div className="rounded-lg border border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/15">
      <button
        className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left"
        onClick={() => setOpen(!open)}
      >
        <ShieldAlert className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <span className="font-semibold text-amber-800 dark:text-amber-300">
          Retenidas por verificación de ML: {data.total}
        </span>
        <span className="text-sm text-[var(--color-text-secondary)]">
          {Object.entries(data.counts)
            .map(([s, n]) => `${STAGE_LABEL[s] || s}: ${n}`)
            .join(' · ')}
        </span>
        <span className="ml-auto text-[var(--color-text-secondary)]">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {open && (
        <div className="max-h-96 overflow-y-auto border-t border-[var(--color-border)] bg-[var(--color-surface)]">
          {data.cases.map((c) => (
            <div
              key={c.id}
              className={`flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-2 text-sm text-[var(--color-text)] ${STAGE_ROW[c.stage] || ''}`}
            >
              <span
                className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${STAGE_BADGE[c.stage] || ''}`}
              >
                {STAGE_LABEL[c.stage] || c.stage}
              </span>
              {c.thumbnail && (
                <img src={c.thumbnail} alt="" className="h-8 w-8 shrink-0 rounded object-cover" />
              )}
              <span className="min-w-0 flex-1 truncate" title={c.title || c.asin || ''}>
                {c.title || c.asin || c.ml_order_id}
                {c.quantity && c.quantity > 1 ? ` ×${c.quantity}` : ''}
              </span>
              {c.arrived_at_wh && (
                <span
                  className="flex shrink-0 items-center gap-1 rounded bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-900/30 dark:text-sky-300"
                  title="El paquete ya llegó al depósito (escaneado)"
                >
                  <Package className="h-3 w-3" /> en depósito
                </span>
              )}
              <span className="hidden font-mono text-xs text-[var(--color-text-secondary)] sm:inline">
                #{c.ml_order_id}
              </span>
              <span className="shrink-0 text-xs text-[var(--color-text-secondary)]">
                {daysSince(c.detected_at)}d
              </span>
              <span className="shrink-0 font-semibold">${Number(c.amount || 0).toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
